import { useScrollReveal } from '@/hooks/useScrollReveal';

interface ProductSpecsProps {
  category: string;
  price: number;
  tags: string[];
}

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`scroll-reveal ${isVisible ? 'scroll-reveal-visible' : ''} ${delay > 0 ? `scroll-reveal-delay-${Math.min(delay, 6)}` : ''} ${className}`}
    >
      {children}
    </div>
  );
}

export default function ProductSpecs({ category, price, tags }: ProductSpecsProps) {
  const hasBluetooth = tags.some((t) => t.includes('藍牙'));
  const isRechargeable = tags.includes('充電式');
  const isMini = tags.includes('迷你') || tags.includes('輕巧');
  const isPowerful = tags.includes('大功率') || tags.includes('重度聽損');;
  const isWaterproof = tags.includes('防水');
  const isChild = tags.includes('兒童專用');

  const specs = [
    { label: '類型', value: category },
    { label: '頻率響應', value: '250Hz - 8,000Hz' },
    {
      label: '最大增益',
      value: isPowerful ? '82dB SPL' : isMini ? '45dB SPL' : '65dB SPL',
    },
    {
      label: '電池類型',
      value: isRechargeable ? '鋰電池（充電式）' : '鋅空電池 312',
    },
    {
      label: '續航時間',
      value: isRechargeable ? '充電盒 24 小時 / 單次 6 小時' : '約 7-10 天',
    },
    {
      label: '藍牙版本',
      value: hasBluetooth ? 'Bluetooth 5.0' : '不支援',
    },
    {
      label: '防水等級',
      value: isWaterproof ? 'IP57' : 'IP33',
    },
    {
      label: '重量',
      value: isMini ? '1.8g' : isChild ? '2.5g' : '3.2g',
    },
    { label: '保固期', value: '2 年原廠保固' },
    { label: '試戴服務', value: '免費 30 天試戴' },
    { label: '售後調校', value: '終生免費調校服務' },
  ];

  return (
    <SectionReveal>
      <div className="bg-white rounded-2xl border border-[#E8E0D6] p-6 md:p-10">
        <div className="flex items-center gap-2 mb-6 md:mb-8">
          <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-list-check-2 text-[#1B3D2A]" />
            </div>
          </div>
          <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">Specifications</span>
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-[#1B3D2A] mb-6 md:mb-8">
          產品規格
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {specs.map((spec, i) => (
            <div
              key={spec.label}
              className={`flex items-center justify-between py-4 px-1 ${
                i !== specs.length - 1 ? 'border-b border-[#E8E0D6]/50' : ''
              } ${i % 2 === 0 && i !== specs.length - 1 ? 'md:border-r md:pr-6 md:mr-6' : ''} ${i % 2 === 1 ? 'md:pl-6' : ''}`}
            >
              <span className="text-sm text-[#5A6B5A]">{spec.label}</span>
              <span className="text-sm font-semibold text-[#1B3D2A] text-right ml-4">
                {spec.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </SectionReveal>
  );
}